// src/App.js
import React, { useState, useEffect } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Select, MenuItem,
  FormControl, InputLabel, Button, Box, Alert, Skeleton
} from '@mui/material';
import './App.css';

const API_BASE = 'https://taxbasecx.sinewave.co.in/api';  // Change to your server

function App() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ totalLogins: 0, todayLogins: 0 });
  const [users, setUsers] = useState([]);
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [endDate, setEndDate] = useState(new Date());
  const [selectedUser, setSelectedUser] = useState('');
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [extraStats, setExtraStats] = useState({});

  useEffect(() => {
    fetchStats();
    fetchLogs();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [startDate, endDate, selectedUser]);

  const fetchStats = () => {
    fetch(`${API_BASE}/stats/`)
      .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(setStats)
      .catch(() => setError('Failed to load stats'))
      .finally(() => setLoading(false));
  };

  const fetchLogs = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', formatDate(startDate));
    if (endDate) params.append('endDate', formatDate(endDate));
    if (selectedUser) params.append('userId', selectedUser);

    fetch(`${API_BASE}/usage/?${params}`)
      .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(data => {
        setLogs(data);
        setError('');
        const uniq = [...new Set(data.map(d => d.user_id))].sort();
        setUsers(uniq);

        const dayMap = data
          .filter(d => d.event_type === 'Login')
          .reduce((acc, d) => {
            const day = d.timestamp.split('T')[0];
            acc[day] = (acc[day] || 0) + 1;
            return acc;
          }, {});

        const chart = Object.entries(dayMap)
          .map(([date, logins]) => ({ date, logins }))
          .sort((a, b) => a.date.localeCompare(b.date));
        setChartData(chart);

        const logins = data.filter(d => d.event_type === 'Login');
        const uniqueUsers = new Set(logins.map(d => d.user_id)).size;
        const hourCounts = logins.reduce((acc, d) => {
          const hour = new Date(d.timestamp).getHours();
          acc[hour] = (acc[hour] || 0) + 1;
          return acc;
        }, {});
        const peakHour = Object.entries(hourCounts).sort((a, b) => b[1] - a[1])[0];
        const avgSession = logins.length > 0 ? (logins.reduce((sum, d) => {
          const duration = d.session_duration || 0;
          return sum + duration;
        }, 0) / logins.length / 60).toFixed(1) : 0;

        setExtraStats({
          totalUsers: uniqueUsers,
          peakHour: peakHour ? `${peakHour[0]}:00` : 'N/A',
          avgSession: avgSession
        });
      })
      .catch(() => setError('Failed to load logs'))
      .finally(() => setLoading(false));
  };

  const formatDate = d => d.toISOString().split('T')[0];

  const todayLogins = logs.filter(l =>
    l.event_type === 'Login' &&
    new Date(l.timestamp).toDateString() === new Date().toDateString()
  );

  const trend = chartData.length > 1
    ? (chartData[chartData.length - 1].logins > chartData[0].logins ? 'Increasing' : 'Decreasing')
    : 'Stable';

  return (
    <div className="dashboard">
      {/* Horizontal Header */}
      <header className="header">
        <div className="header-left">
          <Typography variant="h5" className="title">TaxbaseCX</Typography>
        </div>
        <div className="header-center">
          <Typography variant="h6" className="dashboard-title">
            Usage Dashboard
          </Typography>
        </div>
        <div className="header-right">
          <div className="date-range">
            {formatDate(startDate)} - {formatDate(endDate)}
          </div>
        </div>
      </header>

      {/* Error */}
      {error && <Alert severity="error" sx={{ mx: 2, my: 1, borderRadius: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Box className="filters-card">
        <DatePicker selected={startDate} onChange={setStartDate} dateFormat="yyyy-MM-dd" customInput={<input className="date-input" placeholder="Start" />} />
        <DatePicker selected={endDate} onChange={setEndDate} minDate={startDate} dateFormat="yyyy-MM-dd" customInput={<input className="date-input" placeholder="End" />} />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>User</InputLabel>
          <Select value={selectedUser} label="User" onChange={e => setSelectedUser(e.target.value)} sx={{ bgcolor: 'white', borderRadius: 2 }}>
            <MenuItem value="">All</MenuItem>
            {users.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="contained" className="apply-btn" onClick={fetchLogs}>
          Apply
        </Button>
      </Box>

      {/* Stats Grid */}
      <Box className="stats-grid">
        {loading ? (
          Array(6).fill().map((_, i) => (
            <Skeleton key={i} variant="rounded" height={110} sx={{ borderRadius: 3 }} />
          ))
        ) : (
          <>
            <Card className="stat-card"><CardContent><Typography variant="subtitle2" color="text.secondary">Total Logins</Typography><Typography variant="h4" className="stat-value">{stats.totalLogins.toLocaleString()}</Typography></CardContent></Card>
            <Card className="stat-card"><CardContent><Typography variant="subtitle2" color="text.secondary">Today's Logins</Typography><Typography variant="h4" className="stat-value">{stats.todayLogins}</Typography></CardContent></Card>
            <Card className="stat-card"><CardContent><Typography variant="subtitle2" color="text.secondary">Active Users</Typography><Typography variant="h4" className="stat-value">{extraStats.totalUsers || 0}</Typography></CardContent></Card>
            <Card className="stat-card"><CardContent><Typography variant="subtitle2" color="text.secondary">Peak Hour</Typography><Typography variant="h4" className="stat-value">{extraStats.peakHour || 'N/A'}</Typography></CardContent></Card>
            <Card className="stat-card"><CardContent><Typography variant="subtitle2" color="text.secondary">Avg Session</Typography><Typography variant="h4" className="stat-value">{extraStats.avgSession || 0} min</Typography></CardContent></Card>
            <Card className="stat-card trend-card">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">Trend</Typography>
                <Typography
                  variant="h5"
                  className={trend === 'Increasing' ? 'trend-increasing' : trend === 'Decreasing' ? 'trend-decreasing' : 'trend-stable'}
                >
                  {trend}
                </Typography>
              </CardContent>
            </Card>
          </>
        )}
      </Box>

      {/* Main Content */}
      <Box className="content-grid">
        <Card className="chart-card">
          <CardContent>
            <Typography variant="h6" className="chart-title">Daily Login Trend</Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 3 }} />
            ) : chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorLogins" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2196f3" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#2196f3" stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e0e0e0', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Area type="monotone" dataKey="logins" stroke="#2196f3" strokeWidth={2.5} fillOpacity={1} fill="url(#colorLogins)" dot={{ r: 4 }} activeDot={{ r: 6 }} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" align="center" sx={{ py: 10 }}>No data</Typography>
            )}
          </CardContent>
        </Card>

        <Card className="table-card">
          <CardContent>
            <Typography variant="h6" className="table-title">Today's Logins</Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 3 }} />
            ) : todayLogins.length > 0 ? (
              <TableContainer sx={{ maxHeight: 260 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow className="table-header">
                    <TableCell><strong>User</strong></TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Time</strong></TableCell>
                    <TableCell><strong>Device</strong></TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {todayLogins.slice(0, 8).map((l, i) => (
                      <TableRow key={i} className="table-row">
                        <TableCell>{l.user_id}</TableCell>
                        <TableCell>{l.username}</TableCell>
                        <TableCell>{new Date(l.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</TableCell>
                        <TableCell className="device-cell">{l.device_info}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" align="center" sx={{ py: 8 }}>No logins today</Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </div>
  );
}

export default App;